import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface Props {
  src: string;
  /** Source-file time, not the clip-relative timeline time. */
  sourceTime: number;
  /** Audio remains owned by the editor renderer; this layer is always muted. */
  isPlaying: boolean;
  bounds?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

/**
 * Memory-safe local preview for files too large to clone into the Creatomate
 * iframe. The editor renderer remains the playback clock and audio source;
 * this muted native video follows it through the File's object URL.
 */
export const NativeLocalVideoPreview: React.FC<Props> = ({
  src,
  sourceTime,
  isPlaying,
  bounds,
}) => {
  const ref = useRef<HTMLVideoElement>(null);

  const syncTime = useCallback(() => {
    const video = ref.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) return;
    const requested = Number.isFinite(sourceTime) ? Math.max(0, sourceTime) : 0;
    const target = Number.isFinite(video.duration)
      ? Math.min(requested, video.duration)
      : requested;
    // Let the two media clocks run normally during playback; only correct
    // visible drift. Paused seeks should be frame-accurate.
    const tolerance = isPlaying ? 0.35 : 0.02;
    if (Math.abs(video.currentTime - target) > tolerance) {
      video.currentTime = target;
    }
  }, [isPlaying, sourceTime]);

  useEffect(() => {
    syncTime();
  }, [syncTime]);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (isPlaying) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isPlaying, src]);

  return (
    <Video
      ref={ref}
      src={src}
      muted
      playsInline
      preload="metadata"
      aria-label="Local video preview"
      data-testid="native-local-video-preview"
      style={bounds ?? { inset: 0 }}
      onLoadedMetadata={syncTime}
      onCanPlay={() => {
        syncTime();
        if (isPlaying) void ref.current?.play().catch(() => undefined);
      }}
    />
  );
};

const Video = styled.video`
  position: absolute;
  /* Above the in-flow Creatomate iframe, below Stage's z-index: 1 controls. */
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
  pointer-events: none;
`;
